import { BehaviorSubject, distinct } from 'rxjs'

type TabManagerEvents =
	| { eventType: 'new-tab'; tabId: string; hasFocus: boolean }
	| { eventType: 'close-tab'; tabId: string; lastTab: boolean }
	| { eventType: 'flow-completed'; tabId: string }
	| { eventType: 'active-tab'; tabId: string }
	| { eventType: 'sync-active-tab'; tabId: string }

type TabManagerMessages = MessageEvent<TabManagerEvents>

class TabManagerService {
	#activeTabId: string | null = null
	#tabId: string | null = null
	#tabStatusSubject = new BehaviorSubject<
		'inactive' | 'active' | 'last-active' | 'completed'
	>('inactive')
	#channel = new BroadcastChannel('monthio:tab-manager')
	#tabIds = new Set<string>()
	currentTabActiveSubject = new BehaviorSubject<boolean>(false)

	public setTab = (applicantId: string | null, sessionId: string | null) => {
		if (applicantId === null || sessionId === null) {
			return
		}
		const tabId = `${applicantId}-${sessionId}-${Date.now()}`
		this.#tabId = tabId
		const hasFocus =
			document.hasFocus() || document.visibilityState === 'visible'
		this.#dispatchEvent({
			eventType: 'new-tab',
			tabId,
			hasFocus,
		})
		this.#tabIds.add(tabId)
		if (hasFocus) {
			this.#activeTabId = tabId
			this.#tabStatusSubject.next('active')
			return
		}
		this.#dispatchEvent({
			eventType: 'sync-active-tab',
			tabId,
		})
	}

	public initialize = () => {
		this.#channel.onmessage = async (event: TabManagerMessages) => {
			if (this.#tabId === null) {
				return
			}
			const { data } = event
			const { eventType, ...eventData } = data
			if (eventType !== 'close-tab') {
				this.#tabIds.add(eventData.tabId)
				this.#activeTabId = eventData.tabId
			}
			switch (eventType) {
				case 'sync-active-tab':
					if (
						this.#activeTabId === this.#tabId &&
						eventData.tabId !== this.#tabId
					) {
						this.#dispatchEvent({
							eventType: 'active-tab',
							tabId: this.#tabId,
						})
					}
					break
				case 'new-tab':
				case 'active-tab':
					this.#tabStatusSubject.next(
						eventData.tabId === this.#tabId ? 'active' : 'inactive',
					)
					if (eventData.tabId !== this.#tabId) {
						if (document.hasFocus()) {
							this.#dispatchEvent({
								eventType: 'active-tab',
								tabId: eventData.tabId,
							})
							return
						}

						if (data.eventType === 'new-tab' && !data.hasFocus) {
							return
						}

						this.currentTabActiveSubject.next(true)
					}
					break
				case 'close-tab':
					this.#tabIds.delete(eventData.tabId)
					break
				case 'flow-completed':
					this.#tabStatusSubject.next('completed')
					break
			}
		}

		return () => {
			this.#channel.close()
		}
	}

	public get tabStatus() {
		return this.#tabStatusSubject.asObservable().pipe(distinct())
	}

	public setCurrentTabActive = () => {
		this.#dispatchEvent({
			eventType: 'active-tab',
			tabId: this.#tabId!,
		})
		location.reload()
	}

	public completeFlow = () => {
		this.#dispatchEvent({
			eventType: 'flow-completed',
			tabId: this.#tabId!,
		})
	}

	public closeTab = () => {
		this.#dispatchEvent({
			eventType: 'close-tab',
			tabId: this.#tabId!,
			lastTab: this.#tabIds.size === 1,
		})
	}

	#dispatchEvent = (event: TabManagerEvents) => {
		this.#channel.postMessage(event)
	}
}

export { TabManagerService as TabManagerServiceTest }

export const tabManagerService = new TabManagerService()
